<script setup lang="ts">
import { inject, ref } from 'vue'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { ArrowLeft } from 'lucide-vue-next'
import { cn } from '@/lib/utils'
import { KEY_IS_MOBILE } from '@/keys/keys'

interface Props {
  visible: boolean
  title?: string
  description?: string
  maxWidth?: string // e.g., 'max-w-5xl'
  height?: string // e.g., 'h-[700px]'
  showBackButton?: boolean
  hasSidebar?: boolean
  sidebarClass?: string
  contentClass?: string
  mainClass?: string
  zIndex?: number
  noPaddingMobile?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  showBackButton: true,
  hasSidebar: false,
  maxWidth: 'max-w-5xl',
  height: 'h-[700px]',
  sidebarClass: '',
  contentClass: '',
  mainClass: '',
  zIndex: 2000,
  noPaddingMobile: false
})

const isMobile = inject(KEY_IS_MOBILE, ref(false))

const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void
  (e: 'close'): void
}>()

function handleClose() {
  emit('update:visible', false)
  emit('close')
}

function handleBackdropClick(e: MouseEvent) {
  if (!isMobile.value) {
    e.stopPropagation()
  }
}

function handleOutsideInteraction(e: Event) {
  e.preventDefault()
}
</script>

<template>
  <Dialog :open="props.visible" @update:open="emit('update:visible', $event)" :modal="false">
    <!-- Manual backdrop for better control over z-index and click behavior -->
    <Teleport to="body">
      <div
        v-if="props.visible"
        class="fixed inset-0 bg-black/80 transition-opacity animate-in fade-in duration-300 pointer-events-auto"
        :style="{ zIndex: props.zIndex }"
        @click="handleBackdropClick"
      />
    </Teleport>
    
    <DialogContent 
      :style="{ zIndex: props.zIndex }"
      :class="cn(
        'w-full p-0 overflow-hidden border-none shadow-2xl rounded-2xl flex flex-col max-md:h-[100dvh] max-md:max-h-[100dvh] max-md:rounded-none [&>button]:hidden text-gray-900 dark:text-gray-100 mobile-slide-exit',
        props.maxWidth,
        props.height,
        props.hasSidebar ? 'md:flex-row' : '',
        props.contentClass
      )"
      @pointer-down-outside="handleOutsideInteraction"
      @interact-outside="handleOutsideInteraction"
    >
      <!-- Side Navigation Layout -->
      <template v-if="props.hasSidebar">
        <aside :class="cn('w-full md:w-64 bg-zinc-50 dark:bg-black/20 border-r border-border px-4 pt-2 pb-0 md:p-6 flex flex-col gap-2 flex-shrink-0', props.sidebarClass)">
          <!-- Back Button Section -->
          <div v-if="props.showBackButton" class="mb-1 md:mb-6 -ml-2">
            <button 
              class="flex items-center gap-3 h-8 md:h-10 px-2 md:px-3 text-gray-900 dark:text-white hover:bg-white dark:hover:bg-white/5 rounded-xl font-bold transition-all group"
              @click="handleClose"
            >
              <div class="flex h-6 w-6 md:h-7 md:w-7 items-center justify-center rounded-lg bg-[hsl(var(--modal-accent-bg))] dark:bg-[hsl(var(--modal-accent-bg)/0.1)] text-[hsl(var(--modal-accent-text))] dark:text-[hsl(var(--modal-accent-text))] transition-transform duration-200 group-hover:-translate-x-1">
                <ArrowLeft class="w-3.5 h-3.5 md:w-4 md:h-4" />
              </div>
              <span class="text-sm md:text-base">Back</span>
            </button>
          </div>

          <div v-if="props.title || props.description" class="mb-2 md:mb-8 px-2">
            <h2 v-if="props.title" class="text-xl font-bold">{{ props.title }}</h2>
            <p v-if="props.description" class="text-xs text-gray-500 mt-1 leading-relaxed">{{ props.description }}</p>
          </div>
          
          <slot name="sidebar" />
        </aside>

        <div :class="cn('flex-1 flex flex-col overflow-hidden relative', props.mainClass)">
          <div :class="cn('flex-1 flex flex-col px-4 py-0 md:p-8 min-h-0 overflow-x-hidden', props.noPaddingMobile ? 'max-md:px-0' : 'max-md:px-4')">
             <slot />
          </div>
        </div>
      </template>

      <!-- Standard Header Layout (No Sidebar) -->
      <template v-else>
        <header v-if="props.title || props.showBackButton" class="flex flex-col md:relative md:flex-row items-center md:justify-center px-4 pt-2 pb-0 md:p-6 flex-shrink-0 border-b border-border gap-2">
          <div v-if="props.showBackButton" class="w-full md:w-auto md:absolute md:left-4">
            <button 
              class="flex items-center gap-3 h-8 md:h-10 px-2 md:px-3 text-gray-900 dark:text-white hover:bg-white dark:hover:bg-white/5 rounded-xl font-bold transition-all group"
              @click="handleClose"
            >
              <div class="flex h-6 w-6 md:h-7 md:w-7 items-center justify-center rounded-lg bg-[hsl(var(--modal-accent-bg))] dark:bg-[hsl(var(--modal-accent-bg)/0.1)] text-[hsl(var(--modal-accent-text))] dark:text-[hsl(var(--modal-accent-text))] transition-transform duration-200 group-hover:-translate-x-1">
                <ArrowLeft class="w-3.5 h-3.5 md:w-4 md:h-4" />
              </div>
              <span class="text-sm md:text-base">Back</span>
            </button>
          </div>
          
          <h2 v-if="props.title" class="text-xl font-bold tracking-tight text-center px-2">{{ props.title }}</h2>
          <div class="md:absolute md:right-4 flex items-center gap-2">
            <slot name="header-actions" />
          </div>
        </header>

        <!-- Content Body (Standard Layout) -->
        <div :class="cn('flex-1 flex flex-col px-4 py-0 md:p-8 min-h-0 overflow-hidden overflow-x-hidden', props.noPaddingMobile ? 'max-md:px-0' : 'max-md:px-4', props.mainClass)">
           <slot />
        </div>
      </template>
    </DialogContent>
  </Dialog>
</template>
